import React, { createContext, useContext, useState, ReactNode } from 'react';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Box,
  Button,
  Divider,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import EasyModal from '../easymodal/easymodal';

type ConfirmacaoOptions = {
  titulo?:         string;
  mensagem?:       string;
  textoConfirmar?: string;
  textoCancelar?:  string;
};

export type ConfirmarFn = (opcoes?: ConfirmacaoOptions) => Promise<boolean>;

const ConfirmacaoContext = createContext<ConfirmarFn | null>(null);

export const useConfirmacao = (): ConfirmarFn => {
  const ctx = useContext(ConfirmacaoContext);
  if (!ctx) throw new Error('useConfirmacao deve ser usado dentro do ConfirmacaoProvider');
  return ctx;
};

// ─── Dialog interno ───────────────────────────────────────────────────────────

const ConfirmacaoDialog: React.FC<{
  aberto:       boolean;
  opcoes:       ConfirmacaoOptions;
  onFechar:     (res: boolean) => void;
}> = ({ aberto, opcoes, onFechar }) => {
  const theme = useTheme();

  return (
    <EasyModal open={aberto} onClose={() => onFechar(false)} size="dialog" zIndexManual={1000} disableBackdropClose borderRadius={2}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

        {/* Header */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.25,
          px: 2.5, py: 1.75,
          bgcolor: alpha(theme.palette.warning.main, 0.06),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
          flexShrink: 0,
        }}>
          <WarningAmberRoundedIcon sx={{ fontSize: 20, color: 'warning.main', flexShrink: 0 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
            {opcoes.titulo || 'Confirmação'}
          </Typography>
        </Box>

        {/* Mensagem */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', px: 2.5, py: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: '0.875rem' }}>
            {opcoes.mensagem || 'Você tem certeza que deseja continuar?'}
          </Typography>
        </Box>

        <Divider />

        {/* Ações */}
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ px: 2, py: 1.25, flexShrink: 0 }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={() => onFechar(false)}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.82rem',
              borderColor: alpha(theme.palette.divider, 0.9),
              color: 'text.secondary',
              '&:hover': { borderColor: 'text.secondary', bgcolor: alpha(theme.palette.action.hover, 0.5) },
            }}
          >
            {opcoes.textoCancelar || 'Cancelar'}
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            disableElevation
            onClick={() => onFechar(true)}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.82rem',
            }}
          >
            {opcoes.textoConfirmar || 'Confirmar'}
          </Button>
        </Stack>
      </Box>
    </EasyModal>
  );
};

// ─── Provider ─────────────────────────────────────────────────────────────────

export const ConfirmacaoProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [aberto,    setAberto]    = useState(false);
  const [opcoes,    setOpcoes]    = useState<ConfirmacaoOptions>({});
  const [resolveFn, setResolveFn] = useState<(res: boolean) => void>(() => () => {});

  const confirmar: ConfirmarFn = (opts = {}) => {
    setOpcoes(opts);
    setAberto(true);
    return new Promise<boolean>((resolve) => {
      setResolveFn(() => resolve);
    });
  };

  const handleFechar = (res: boolean) => {
    setAberto(false);
    resolveFn(res);
  };

  return (
    <ConfirmacaoContext.Provider value={confirmar}>
      {children}
      <ConfirmacaoDialog aberto={aberto} opcoes={opcoes} onFechar={handleFechar} />
    </ConfirmacaoContext.Provider>
  );
};
